import React from 'react';
import styled from 'styled-components';
import { devLog } from '../utils/devLogger';
import { TOSS_PAYMENTS_CLIENT_KEY, TOSS_PAYMENTS_SECRET_KEY, TOSS_PAYMENTS_SECURE_KEY } from '../services/tossPayments';

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;
  border: 2px solid #7a6647;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-width: 300px;
  font-size: 12px;
`;

const Title = styled.h4`
  margin: 0 0 12px 0;
  color: #7a6647;
  font-size: 14px;
  font-weight: 600;
`;

const KeyItem = styled.div`
  margin-bottom: 8px;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 6px;
`;

const KeyLabel = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const KeyValue = styled.div`
  font-family: monospace;
  color: #666;
  word-break: break-all;
  line-height: 1.3;
`;

const TestButton = styled.button`
  background: #7a6647;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #5d4f3a;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #999;
  
  &:hover {
    color: #666;
  }
`;

interface TossPaymentDebugProps {
  onClose: () => void;
}

export const TossPaymentDebug: React.FC<TossPaymentDebugProps> = ({ onClose }) => {
  const handleTestPayment = async () => {
    try {
      devLog('🧪 토스 페이먼츠 테스트 시작');
      
      // 테스트용 결제 데이터
      const testOrder = {
        amount: 10000,
        orderId: `TEST_ORDER_${Date.now()}`,
        orderName: '테스트 상품',
        customerName: '테스트 구매자',
        customerEmail: 'test@example.com'
      };
      
      devLog('🧪 테스트 주문 데이터:', testOrder);
      
      // 토스 페이먼츠 SDK 로드 확인
      if (!(window as any).TossPayments) {
        const script = document.createElement('script');
        script.src = 'https://js.tosspayments.com/v1/payment';
        document.head.appendChild(script);
        
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }
      
      const tossPayments = (window as any).TossPayments(TOSS_PAYMENTS_CLIENT_KEY);
      
      // 올바른 토스 페이먼츠 API 사용
      const baseUrl = window.location.origin;
      
      await tossPayments.requestPayment('카드', {
        amount: testOrder.amount,
        orderId: testOrder.orderId,
        orderName: testOrder.orderName,
        customerName: testOrder.customerName,
        customerEmail: testOrder.customerEmail,
        successUrl: `${baseUrl}/payment/success?orderId=${testOrder.orderId}&amount=${testOrder.amount}&orderName=${encodeURIComponent(testOrder.orderName)}`,
        failUrl: `${baseUrl}/payment/fail?orderId=${testOrder.orderId}&amount=${testOrder.amount}`
      });
      
    } catch (error) {
      devLog('🧪 테스트 결제 오류:', error);
      alert('테스트 결제 실행 중 오류가 발생했습니다: ' + (error as any).message);
    }
  };

  const handleGoToShoppingPage = () => {
    // 첫 번째 상품으로 이동 (실제로는 존재하는 상품 ID를 사용해야 함)
    window.location.href = '/shopping-request/1';
  };

  const handleLogKeys = () => {
    devLog('🔑 토스 페이먼츠 키 정보:', {
      CLIENT_KEY: TOSS_PAYMENTS_CLIENT_KEY,
      SECRET_KEY: TOSS_PAYMENTS_SECRET_KEY,
      SECURE_KEY: TOSS_PAYMENTS_SECURE_KEY
    });
  };

  return (
    <Container>
      <CloseButton onClick={onClose}>×</CloseButton>
      <Title>🧪 토스 결제 테스트</Title>
      
      <KeyItem>
        <KeyLabel>CLIENT KEY:</KeyLabel>
        <KeyValue>{TOSS_PAYMENTS_CLIENT_KEY}</KeyValue>
      </KeyItem>
      
      <KeyItem>
        <KeyLabel>SECRET KEY:</KeyLabel>
        <KeyValue>{TOSS_PAYMENTS_SECRET_KEY}</KeyValue>
      </KeyItem>
      
      <KeyItem>
        <KeyLabel>SECURE KEY:</KeyLabel>
        <KeyValue>{TOSS_PAYMENTS_SECURE_KEY}</KeyValue>
      </KeyItem>
      
      <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
        <TestButton onClick={handleTestPayment}>
          직접 결제 테스트
        </TestButton>
        <TestButton onClick={handleGoToShoppingPage}>
          쇼핑 페이지로 이동
        </TestButton>
        <TestButton onClick={handleLogKeys}>
          키 정보 콘솔 출력
        </TestButton>
      </div>
    </Container>
  );
};

export default TossPaymentDebug;